import i18n from './index';

type Locale = 'zh-CN' | 'en' | 'zh-TW';

interface CmsCopyResponse {
  data?: Record<string, string>;
}

function isRecordOfString(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== 'object') return false;
  return Object.values(value).every((v) => typeof v === 'string');
}

const ALLOWED_LOCALES = new Set<Locale>(['zh-CN', 'en', 'zh-TW']);
const DEFAULT_TTL_MS = Number(import.meta.env.VITE_I18N_CACHE_TTL_MS ?? 60_000);
const API_BASE = (import.meta.env.VITE_I18N_API_BASE_URL ?? '').replace(/\/$/, '');

interface CacheEntry {
  expireAt: number;
  payload: Record<string, string>;
}

const cache = new Map<string, CacheEntry>();

function getLocale(locale: string): Locale {
  if (ALLOWED_LOCALES.has(locale as Locale)) {
    return locale as Locale;
  }
  return 'en';
}

function getCacheKey(scope: string, locale: string): string {
  return `${scope}::${locale}`;
}

function isExpired(entry: CacheEntry): boolean {
  return Date.now() > entry.expireAt;
}

async function requestCopy(path: string): Promise<Record<string, string>> {
  const target = `${API_BASE}${path}`;
  const res = await fetch(target, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`i18n copy fetch failed: ${res.status} ${res.statusText}`);
  }

  const json = (await res.json()) as CmsCopyResponse | Record<string, unknown>;
  if (json && typeof json === 'object' && 'data' in json && isRecordOfString(json.data)) {
    return json.data;
  }
  if (!json || typeof json !== 'object') {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(json)) {
    if (typeof v === 'string') {
      out[k] = v;
    }
  }
  return out;
}

function injectBundle(locale: Locale, bundle: Record<string, string>): void {
  if (!bundle || Object.keys(bundle).length === 0) {
    return;
  }

  i18n.addResourceBundle(locale, 'translation', bundle, true, true);
}

async function loadScopedCopy(scope: string, locale: Locale, endpoint: string): Promise<void> {
  const cacheKey = getCacheKey(scope, locale);
  const cached = cache.get(cacheKey);
  if (cached && !isExpired(cached)) {
    injectBundle(locale, cached.payload);
    return;
  }

  const payload = await requestCopy(endpoint);
  cache.set(cacheKey, {
    expireAt: Date.now() + DEFAULT_TTL_MS,
    payload,
  });
  injectBundle(locale, payload);
}

export async function loadCommonCopy(localeArg: string): Promise<void> {
  const locale = getLocale(localeArg);
  await loadScopedCopy('common', locale, `/api/i18n/common?locale=${encodeURIComponent(locale)}`);
}

export async function getPageCopy(pageSlug: string, localeArg: string): Promise<void> {
  const locale = getLocale(localeArg);
  await loadScopedCopy(`page:${pageSlug}`, locale, `/api/i18n/page/${encodeURIComponent(pageSlug)}?locale=${encodeURIComponent(locale)}`);
}

export function invalidateCopyCache(scope?: string): void {
  if (!scope) {
    cache.clear();
    return;
  }

  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(scope)) {
      cache.delete(key);
    }
  }
}

export async function triggerRemoteRevalidate(scope?: string): Promise<void> {
  const target = `${API_BASE}/api/i18n/revalidate`;
  try {
    await fetch(target, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scope }),
    });
  } catch (err) {
    console.warn('[i18n] remote revalidate failed', err);
  } finally {
    invalidateCopyCache(scope);
  }
}
